<template>
  <section class="sidebar">
    <header class="sidebar-header flex">
      <template v-if="isSummaryVisible">
        <h2 v-if="isSummaryPopupVisible">
          Oversigt
        </h2>
        <h1 v-else>
          Bekræftelse
        </h1>

        <c-button
          v-if="isSummaryPopupVisible"
          theme="close"
          class="ml-auto"
          @click.prevent="closeSummaryPopup" />
      </template>
      <template v-else>
        <slot name="header"></slot>
      </template>
    </header>

    <div class="sidebar-content relative">
      <template v-if="isSummaryVisible">
        <wardrobe-summary
          ref="wardrobeSummary"
          :is-loading="isFetchingPrice"
          :is-loading-error="fetchPriceError"
          :should-send-doors="shouldSendDoors"
          :should-send-inventory="shouldSendInventory"
          :wardrobe-data="wardrobeForDisplay" />
      </template>
      <template v-else>
        <slot name="content"></slot>
      </template>
    </div>

    <footer class="sidebar-footer">
      <div v-if="isSummaryVisible">
        <div class="flex mt-auto summary-total">
          <strong class="mr-2">Garderobe pris:</strong>
          <div class="ml-auto">
            <span
              v-if="price.total_price_before_discount"
              class="total-price-discount mr-2">
              {{ getPriceFormatted(price.total_price_before_discount) }}
            </span>
          <span class="total-price">
              {{ getPriceFormatted(price.total_price) }}
            </span>
          </div>
        </div>
        <div v-if="!isSummaryPopupVisible">
          <c-button
            v-if="isDebugMode"
            theme="warning"
            class="full-width"
            @click.prevent="exportToFile">
            Export to file
          </c-button>
        </div>
        <div
          v-else
          class="flex">
          <c-button
            v-if="wardrobeList.length > 1"
            theme="warning"
            class="full-width"
            @click.prevent="handleRemoveWardrobe">
            Annuller oprettelse
          </c-button>
          <c-button
            v-if="!isWardrobeNew && !isWardrobeChanged && isWardrobeReadyForCheckout"
            theme="secondary"
            class="full-width"
            @click.prevent="handleBackToSummary">
            Tilbage til oversigt
          </c-button>
          <c-button
            v-if="!isWardrobeNew && isWardrobeChanged && isWardrobeReadyForCheckout"
            theme="orange"
            class="full-width"
            @click.prevent="handleCancelChanges">
            Fortryd ændringer
          </c-button>
        </div>
      </div>
      <template v-if="!isSummaryPopupVisible">
        <slot name="footer"></slot>
      </template>
    </footer>

  </section>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash.debounce';
import WardrobeSummary from '@/components/layout/WardrobeSummary.vue';
import CustomDecimals from '@/utils/CustomDecimals.js';
import StateUtils from '@/utils/StateUtils.js';
import fetchPriceMixin from '@/mixins/fetchPriceMixin';

export default {
  name: 'right-sidebar',
  components: {
    'wardrobe-summary': WardrobeSummary,
  },
  mixins: [fetchPriceMixin],
  inject: ['isDebugMode'],
  props: {
    shouldSendDoors: {
      default: true,
      type: Boolean
    },
    shouldSendInventory: {
      default: true,
      type: Boolean
    },
    viewLocalState: {
      default: () => ({
        doors: {},
        doorsNumberObject: {},
        wardrobe: {}
      }),
      type: Object
    }
  },
  computed: {
    ...mapGetters('application', [
      'isSummaryPopupVisible'
    ]),
    ...mapGetters('creator', [
      'creatorWardrobeData',
      'price',
      'wardrobeId'
    ]),
    ...mapGetters('cart', [
      'wardrobeList'
    ]),
    isSummaryVisible () {
      if (this.isSummaryPopupVisible) {
        return true;
      }

      if (this.$route.query && this.$route.query.wardrobeSummary) {
        return true;
      }

      return false;
    },
    wardrobeForDisplay () {
      let wardrobeData = JSON.parse(JSON.stringify(this.creatorWardrobeData));
      if (!this.isSummaryPopupVisible) {
        return wardrobeData;
      }

      wardrobeData.wardrobe = {
        ...JSON.parse(JSON.stringify(wardrobeData.wardrobe)),
        ...(!!this.viewLocalState && Object.hasOwn(this.viewLocalState, 'wardrobe') ? this.viewLocalState.wardrobe : {})
      };
      wardrobeData.doors = {
        ...JSON.parse(JSON.stringify(wardrobeData.doors)),
        ...(!!this.viewLocalState && Object.hasOwn(this.viewLocalState, 'doors') ? this.viewLocalState.doors : {})
      };
      wardrobeData.doorsNumberObject = {
        ...JSON.parse(JSON.stringify(wardrobeData.doorsNumberObject)),
        ...(!!this.viewLocalState && Object.hasOwn(this.viewLocalState, 'doorsNumberObject') ? this.viewLocalState.doorsNumberObject : {})
      }

      return wardrobeData;
    },
  },
  watch: {
    isSummaryVisible (newVal) {
      if (!newVal) {
        return;
      }

      Vue.nextTick(() => {
        this.isWardrobeNew = StateUtils.isWardrobeNew(this.wardrobeForDisplay, this.wardrobeList);
        if (this.isWardrobeNew) {
          this.isWardrobeChanged = false;
          this.isWardrobeReadyForCheckout = false;
        } else {
          this.isWardrobeReadyForCheckout = this.$store.getters['cart/isWardrobeReadyForCheckout'](this.wardrobeId);
          if (this.isWardrobeReadyForCheckout) {
            this.isWardrobeChanged = StateUtils.isWardrobeChanged(this.wardrobeForDisplay, this.wardrobeList);
          } else {
            this.isWardrobeChanged = false;
          }
        }

        this.debouncedUpdatePrice();
      })
    },
    viewLocalState () {
      if (!this.isSummaryVisible) {
        return;
      }
      Vue.nextTick(() => {
        this.debouncedUpdatePrice();
      })
    }
  },
  data () {
    return {
      isWardrobeChanged: false,
      isWardrobeNew: false,
      isWardrobeReadyForCheckout: false
    }
  },
  mounted () {
    if (this.$route.query.wardrobeSummary) {
      Vue.nextTick(() => {
        this.debouncedUpdatePrice();
      });
    }
  },
  methods: {
    ...mapActions('cart', [
      'removeWardrobe',
      'editExistingWardrobe'
    ]),
    closeSummaryPopup () {
      this.$store.commit('application/setIsSummaryPopupVisible', false);
    },
    getPriceFormatted (price) {
      return CustomDecimals.getPriceFormatted(price);
    },
    exportToFile () {
      try {
        const configData = localStorage.getItem('pandora-creator-autosave');

        if (!configData) {
          console.error('No data fount at key: pandora-creator-autosave');
          return;
        }

        const blob = new Blob([configData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const timestamp = new Date().toISOString().slice(0, 10);
        a.download = `pandora-creator-wardrobe-${timestamp}.json`;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);

        console.log('Export successful.');
      } catch (error) {
        console.error('Error during export:', error);
      }
    },
    debouncedUpdatePrice: debounce(function () {
      this.updatePrice();
    }, 250),
    updatePrice () {
      this.fetchPrice(this.viewLocalState, this.shouldSendDoors, this.shouldSendInventory);
    },
    handleRemoveWardrobe () {
      Vue.swal({
        title: 'Note!',
        text: 'Er du sikker på, at du vil slette dette skab?',
        icon: 'warning',
        confirmButtonText: 'Ja, slet det',
        showCancelButton: true,
        cancelButtonText: 'Annuller'
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }

        this.closeSummaryPopup();
        this.removeWardrobe(this.creatorWardrobeData.id);
        this.$router.push({ name: 'orderSummary' });
      });
    },
    handleCancelChanges () {
      Vue.swal({
        title: 'Note!',
        text: 'Er du sikker på, at du vil annullere dine ændringer? Alle ugemte ændringer i dette skab vil gå tabt.',
        icon: 'warning',
        confirmButtonText: 'Ja, fortryd ændringer',
        showCancelButton: true,
        cancelButtonText: 'Nej, bliv her'
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }

        this.closeSummaryPopup();
        this.editExistingWardrobe(this.wardrobeId);
        this.$router.push({ name: 'orderSummary' });
      });
    },
    handleBackToSummary () {
      this.closeSummaryPopup();
      this.$router.push({ name: 'orderSummary' });
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (to.query.wardrobeSummary && !from.query.wardrobeSummary) {
      Vue.nextTick(() => {
        this.debouncedUpdatePrice();
      });
    }
    next();
  }
}
</script>
